import CONFIG from "./config";

export const isEmptyString = (value: string | undefined): boolean => {
  if (!value || value === null || value === undefined || value === "") {
    return true;
  }

  return false;
};

export const isValidEmail = (email: string): boolean => {
  return (
    email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ) !== null
  );
};

const formatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: "auto",
});

const DIVISIONS = [
  { amount: 60, name: "seconds" },
  { amount: 60, name: "minutes" },
  { amount: 24, name: "hours" },
  { amount: 7, name: "days" },
  { amount: 4.34524, name: "weeks" },
  { amount: 12, name: "months" },
  { amount: Number.POSITIVE_INFINITY, name: "years" },
];

export const formatTimeAgo = (date: Date) => {
  let duration = (date.getTime() - new Date().getTime()) / 1000;

  for (let i = 0; i <= DIVISIONS.length; i++) {
    const division = DIVISIONS[i];
    if (Math.abs(duration) < division.amount) {
      return formatter.format(
        Math.round(duration),
        division.name as Intl.RelativeTimeFormatUnit
      );
    }
    duration /= division.amount;
  }
};

export const formatDuration = (durationMs: number) => {
  const hours = Math.floor(durationMs / (1000 * 60 * 60));
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const formatDateTime = (date: Date, separator = " ") =>
  date.toLocaleDateString() + separator + date.toLocaleTimeString();

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const toTitleCase = (str: string): string => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const calculateAmount = (credits: number) => {
  const amount = credits / 100;
  let discount = 0;
  if (credits >= 5000 && credits < 10000) {
    discount = 0.05;
  } else if (credits >= 10000 && credits < 20000) {
    discount = 0.1;
  } else if (credits >= 20000 && credits < 50000) {
    discount = 0.15;
  } else if (credits >= 50000) {
    discount = 0.2;
  }
  const discountedAmount = amount - discount * amount;

  return { amount, discountedAmount: discountedAmount.toFixed(2) };
};

export const getInitials = (string: string) => {
  const names = string.split(" ");
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export const openPcldInViewer = (s3Url: string) => {
  window.open(CONFIG.VIEWER_URL + "/?s=" + s3Url, "_blank")?.focus();
};

export const openViewerV2 = (outputId: string) => {
  window.open(`${CONFIG.VIEWER_V2_URL}/${outputId}`, "_blank")?.focus();
};

const sortedOutputs = ["BINSEG", "FULLSEG", "DEM", "DTM", "ORTHO", "TEXTURE"];

export const outputSort = (a: ScanOutput, b: ScanOutput) => {
  return sortedOutputs.indexOf(a.type) - sortedOutputs.indexOf(b.type);
};

export const arraySum = (arr: number[]): number => {
  return arr.reduce((a, b) => a + b, 0);
};

export const checkNumberInOffset = (x: number, num: number, offset: number) => {
  return num - offset <= x && x <= num + offset;
};

export const tabA11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export const vh = (percent: number) => {
  const h = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight || 0
  );
  return (percent * h) / 100;
};

export const WORKSPACE_USER_ROLES = ["READER", "WRITER", "OWNER", "ADMIN", "CUSTOM"];

export const isExternalPublicWorkspace = (activeWorkspace: Workspace) => {
  return !activeWorkspace.createdAt;
};

export const PROJECT_YOUTUBE_TUTORIALS = new Map<string, string>([
  ["Upload Data", "SuTVBYDlL3Y"],
  ["Select Outputs", "Dlj06Cg4y1o"],
  ["Advanced Settings", "MKP8u97ULQA"],
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isInvalidExif = (exifData: any) => {
  return (
    !exifData ||
    exifData === undefined ||
    !exifData.latitude ||
    !exifData.longitude
  );
};

export function countInArray(
  array: Array<number | string>,
  what: number | string
) {
  let count = 0;
  for (let i = 0; i < array.length; i++) {
    if (array[i] === what) {
      count += 1;
    }
  }
  return count;
}

export const WGS84_PROJ4 = "+proj=longlat +datum=WGS84 +no_defs +type=crs";

export const WGS84_CRS: CRS = {
  area: "World.",
  authority: "EPSG",
  bbox: [90, -180, -90, 180],
  code: "4326",
  name: "WGS 84",
  proj4: "+proj=longlat +datum=WGS84 +no_defs +type=crs",
  unit: "degree (supplier to define representation)",
};

export const dmsToDecimal = (
  degrees: number,
  minutes: number,
  seconds: number,
  direction: string
): number => {
  let result = degrees + minutes / 60 + seconds / 3600;
  if (direction === "S" || direction === "W") {
    result *= -1;
  }
  return result;
};

export const dmToDecimal = (
  degrees: number,
  minutes: number,
  direction: string
): number => {
  let result = degrees + minutes / 60;
  if (direction === "S" || direction === "W") {
    result *= -1;
  }
  return result;
};

export function convertDMStoLatLng(gpsCoordinate: string): number | undefined {
  const dmRegex = /^\d+,\d+(\.\d+)?[NSEW]$/;
  const dmsRegex = /^\d+,\d+,\d+(\.\d+)?[NSEW]$/;

  let decimalCoordinate: number;

  if (dmRegex.test(gpsCoordinate)) {
    // Degrees, Minutes, and decimal minutes
    const [degrees, minutesAndDirection] = gpsCoordinate.split(",");
    const minutes = parseFloat(minutesAndDirection.slice(0, -1));
    const direction = minutesAndDirection.slice(-1);
    decimalCoordinate = dmToDecimal(parseFloat(degrees), minutes, direction);
  } else if (dmsRegex.test(gpsCoordinate)) {
    // Degrees, Minutes, Seconds
    const [degrees, minutes, secondsAndDirection] = gpsCoordinate.split(",");
    const seconds = parseFloat(secondsAndDirection.slice(0, -1));
    const direction = secondsAndDirection.slice(-1);
    decimalCoordinate = dmsToDecimal(
      parseFloat(degrees),
      parseFloat(minutes),
      seconds,
      direction
    );
  } else {
    return undefined;
  }

  return decimalCoordinate;
}

export function convertLatitudeFromXMP(
  gpsCoordinate: string
): number | undefined {
  const ddRegex = /^-?\d+(\.\d+)?$/;
  let decimalCoordinate: number;
  if (ddRegex.test(gpsCoordinate)) {
    decimalCoordinate = parseFloat(gpsCoordinate);
    if (Math.abs(decimalCoordinate) <= 90) {
      return decimalCoordinate;
    }
  }
  return convertDMStoLatLng(gpsCoordinate);
}

export function convertLongitudeFromXMP(
  gpsCoordinate: string
): number | undefined {
  const ddRegex = /^-?\d+(\.\d+)?$/;
  let decimalCoordinate: number;
  if (ddRegex.test(gpsCoordinate)) {
    decimalCoordinate = parseFloat(gpsCoordinate);
    if (Math.abs(decimalCoordinate) <= 180) {
      return decimalCoordinate;
    }
  }
  return convertDMStoLatLng(gpsCoordinate);
}

export function convertAltitudeFromXMP(
  altitudeValue: string
): number | undefined {
  const numberRegex = /^-?\d+(\.\d+)?$/;
  const fractionRegex = /(\d+)\/(\d+)/;

  if (numberRegex.test(altitudeValue)) {
    return parseFloat(altitudeValue);
  }

  const matches = fractionRegex.exec(altitudeValue);

  if (!matches) {
    return undefined;
  }

  const numerator = parseInt(matches[1], 10);
  const denominator = parseInt(matches[2], 10);

  return numerator / denominator;
}

export const formatFileSize = (b: number) => {
  let u = 0;
  const s = 1024;
  while (b >= s || -b >= s) {
    b /= s;
    u++;
  }
  return (u ? b.toFixed(1) + " " : b) + " KMGTPEZY"[u] + "B";
};

export const truncateText = (text: string, maxLength = 20) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  const start = text.slice(0, Math.floor(maxLength / 2));
  const end = text.slice(text.length - Math.floor(maxLength / 2));
  return `${start}...${end}`;
};

export const isEqualToWithError =
    (num: number, value: number, error: number) => (Math.abs(num - value) <= error);

export const createCompareUrl = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
  comparisonEndpoint: string, 
  scan1ID: string | null,
  scan2ID: string | null,
  isPtcldVtour?: boolean
) => {
  const scan1IDStr = scan1ID ? `&scan1=${scan1ID}` : '';
  const scan2IDStr = scan2ID ? `&scan2=${scan2ID}` : '';
  return isPtcldVtour ? `${workspaceSlug}/${projectSlug}/${scan1ID}/viewer?${comparisonEndpoint}` : `${workspaceSlug}/${projectSlug}/${scan1ID}/viewer?${comparisonEndpoint}${scan1IDStr}${scan2IDStr}`;
}

export enum ViewerType {
  VTOUR = "vtour",
  PTCLD = "ptcld",
}

export enum ViewerMode {
  UNISCAN = "uniscan",
  UNISCAN_COMPARE = "uniscan-compare",
  BISCAN = "biscan",
}